import React from 'react';
import {AuthService} from '../api';
import useApiClient, {ErrorInfo} from './useApiClient';

export type PasswordSetLinkState =
  | {status: 'idle'}
  | {status: 'sending'}
  /** `email` is the address the server mailed: the account's own. */
  | {status: 'sent'; email: string}
  /** 429: three links in the past hour already (backend throttle). */
  | {status: 'rateLimited'}
  | {status: 'error'};

/**
 * Emails a signed-in, password-less user a link to set a password
 * (backend#263). Accounts made with Google, Apple or Facebook sign-in have no
 * password, so the change-email and change-password forms, which both ask for
 * the current one, cannot be used; the link sets one without signing out.
 * Silent: each app renders the outcome from `state` itself.
 */
const usePasswordSetLink = () => {
  const {fetch} = useApiClient();
  const [state, setState] = React.useState<PasswordSetLinkState>({
    status: 'idle',
  });

  // One request at a time whatever the caller does: each costs one of the
  // user's three links an hour.
  const inFlight = React.useRef(false);

  const send = React.useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setState({status: 'sending'});
    try {
      const {email} = await fetch(AuthService.passwordSetLink(), {
        silent: true,
      });
      setState({status: 'sent', email});
    } catch (error) {
      setState(
        (error as ErrorInfo)?.statusCode === 429
          ? {status: 'rateLimited'}
          : {status: 'error'},
      );
    } finally {
      inFlight.current = false;
    }
  }, [fetch]);

  return {state, send};
};

export default usePasswordSetLink;
